import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Square, Clock } from 'lucide-react'
import { toast } from 'sonner'
import { useQueryClient } from 'react-query'
import { Button } from '@/components/ui/button'
import { useTimerStore } from '@/store/timerStore'
import { timeEntryService } from '@/services/timeEntryService'
import { refreshActiveTimer } from '@/lib/useActiveTimerSync'
import TimerStopDialog from './TimerStopDialog'
import { cn } from '@/lib/utils'

const formatElapsed = (startTime) => {
  const secs = Math.floor((Date.now() - new Date(startTime)) / 1000)
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = secs % 60
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

const formatDuration = (startTime) => {
  const secs = Math.floor((Date.now() - new Date(startTime)) / 1000)
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  if (h && m) return `${h}h ${m}m`
  if (h) return `${h}h`
  return `${m}m`
}

const TimerBar = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { activeTimer, stopTimer } = useTimerStore()
  const [elapsed, setElapsed] = useState('')
  const [stopOpen, setStopOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!activeTimer?.startTime) return
    const interval = setInterval(() => setElapsed(formatElapsed(activeTimer.startTime)), 1000)
    setElapsed(formatElapsed(activeTimer.startTime))
    return () => clearInterval(interval)
  }, [activeTimer?.startTime])

  if (!activeTimer) return null

  const handleStopConfirm = async (note) => {
    setLoading(true)
    try {
      await timeEntryService.stop(activeTimer.id, note)
      stopTimer()
      queryClient.invalidateQueries(['projects', String(activeTimer.projectId), 'tasks'])
      queryClient.invalidateQueries('time-summary')
      toast.success('Timer stopped')
      setStopOpen(false)
    } catch (err) {
      if (err.response?.status === 404) {
        // Already stopped elsewhere (another tab, or an AI agent via MCP)
        refreshActiveTimer(queryClient)
        setStopOpen(false)
        toast.info('That timer was already stopped elsewhere')
        return
      }
      toast.error(err.response?.data?.error || 'Failed to stop timer')
    } finally {
      setLoading(false)
    }
  }

  const handleNavigate = () => {
    if (activeTimer.projectId) {
      navigate(`/projects/${activeTimer.projectId}/tasks/${activeTimer.taskId}`)
    }
  }

  return (
    <>
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-foreground text-background rounded-full px-4 py-2 shadow-lg border border-border/20">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 animate-pulse text-primary-foreground/70" />
          <span className="font-mono text-sm font-medium tabular-nums">{elapsed}</span>
        </div>
        <div className="w-px h-4 bg-background/20" />
        <button
          onClick={handleNavigate}
          className="text-sm text-background/80 hover:text-background transition-colors truncate max-w-[200px]"
        >
          {activeTimer.taskTitle || 'Timer running'}
        </button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setStopOpen(true)}
          className="h-7 rounded-full text-background/80 hover:text-background hover:bg-background/10 text-xs px-2"
        >
          <Square className="h-3 w-3 mr-1 fill-current" />
          Stop
        </Button>
      </div>

      <TimerStopDialog
        open={stopOpen}
        onOpenChange={setStopOpen}
        taskTitle={activeTimer.taskTitle}
        elapsedLabel={formatDuration(activeTimer.startTime)}
        onConfirm={handleStopConfirm}
        loading={loading}
      />
    </>
  )
}

export default TimerBar
